import React from 'react';
import { twMerge } from 'tailwind-merge';
import { ORDER_STOP_ORDERS, PRO_ORDERS } from '../../../constants/featureFlags.ts';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag.ts';
import { cn } from '../../../lib/classNameHelpers.ts';
import { tifOptions } from '../../../lib/tifOptions.ts';
import { postOnlyTooltip, reduceOnlyTooltip, tpSlTooltip } from '../../../lib/tooltips';
import { OrderType, TimeInForce } from '../../../types/orders';
import { Button } from '../../catalyst-ui-kit/button';
import { PlainSelect } from '../../SimpleSelect/SimpleSelect';
import { Tooltip } from '../../Tooltip/Tooltip';
import { useTradeFormStore } from './TradeFormState.ts';

const TradeFormOptionsToggle = ({
  checked,
  onChange,
  label,
  disabled,
  children,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
  disabled?: boolean;
  children: React.ReactNode;
}) => (
  <Button
    size="xs"
    color="tertiary"
    role="checkbox"
    aria-label={label}
    disabled={disabled}
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    className={cn(
      checked ? 'text-contentPrimary' : 'text-contentSecondary dark:text-contentSecondary'
    )}
  >
    {children}
  </Button>
);

export interface TradeFormOptionsUiProps {
  className?: string;
  orderType: OrderType;
  reduceOnly: boolean;
  setReduceOnly: (value: boolean) => void;
  postOnly: boolean;
  setPostOnly: (value: boolean) => void;
  showTpSl: boolean;
  setShowTpSl: (value: boolean) => void;
  tif: TimeInForce;
  setTif: (value: TimeInForce) => void;
  orderTpSlEnabled: boolean;
  proOrderEnabled: boolean;
}

export const TradeFormOptionsUi: React.FC<TradeFormOptionsUiProps> = ({
  className,
  orderType,
  reduceOnly,
  setReduceOnly,
  postOnly,
  setPostOnly,
  showTpSl,
  setShowTpSl,
  tif,
  setTif,
  orderTpSlEnabled,
  proOrderEnabled,
}) => {
  const selectedTifOption = tif ? tifOptions[tif] : undefined;
  return (
    <div className={twMerge('flex items-center justify-between', className)}>
      <div className="flex items-center gap-1">
        <Tooltip content={reduceOnlyTooltip()}>
          <TradeFormOptionsToggle
            label="Toggle reduce"
            checked={reduceOnly}
            onChange={setReduceOnly}
          >
            Reduce
          </TradeFormOptionsToggle>
        </Tooltip>

        {orderType === 'LIMIT' && (
          <Tooltip content={postOnlyTooltip()}>
            <TradeFormOptionsToggle
              disabled={selectedTifOption?.disabledWithPostOnly}
              label="Toggle post"
              checked={postOnly}
              onChange={setPostOnly}
            >
              Post
            </TradeFormOptionsToggle>
          </Tooltip>
        )}

        <Tooltip
          content={tpSlTooltip({
            isEnabled: orderTpSlEnabled,
            hasProOrders: proOrderEnabled,
          })}
        >
          <TradeFormOptionsToggle
            label="Toggle TP/SL"
            checked={showTpSl}
            onChange={setShowTpSl}
            disabled={!orderTpSlEnabled}
          >
            TP/SL
          </TradeFormOptionsToggle>
        </Tooltip>
      </div>

      {orderType === 'LIMIT' && (
        <div className="flex items-center gap-2">
          <PlainSelect
            className="text-xs"
            options={Object.values(tifOptions).map((option) => ({
              value: option.value,
              label: option.dropdownLabel,
              disabled: option.disabledWithPostOnly && postOnly,
            }))}
            selected={selectedTifOption}
            onChange={(option) => option && setTif(option.value)}
          />
        </div>
      )}
    </div>
  );
};

export const TradeFormOptions: React.FC<{ className?: string }> = ({ className }) => {
  const orderType = useTradeFormStore((state) => state.orderType);
  const reduceOnly = useTradeFormStore((state) => state.reduceOnly);
  const setReduceOnly = useTradeFormStore((state) => state.setReduceOnly);
  const postOnly = useTradeFormStore((state) => state.postOnly);
  const setPostOnly = useTradeFormStore((state) => state.setPostOnly);
  const showTpSl = useTradeFormStore((state) => state.showTpSl);
  const setShowTpSl = useTradeFormStore((state) => state.setShowTpSl);
  const tif = useTradeFormStore((state) => state.tif);
  const setTif = useTradeFormStore((state) => state.setTif);
  const { value: orderTpSlEnabled } = useFeatureFlag(ORDER_STOP_ORDERS);
  const { value: proOrderEnabled } = useFeatureFlag(PRO_ORDERS);

  return (
    <TradeFormOptionsUi
      className={className}
      orderType={orderType}
      reduceOnly={reduceOnly}
      setReduceOnly={setReduceOnly}
      postOnly={postOnly}
      setPostOnly={setPostOnly}
      showTpSl={showTpSl}
      setShowTpSl={setShowTpSl}
      tif={tif}
      setTif={setTif}
      orderTpSlEnabled={orderTpSlEnabled}
      proOrderEnabled={proOrderEnabled}
    />
  );
};
